import type { ChangeEvent } from "react";

export type SanitizeInputOptions = {
  /** Keep line breaks (for textarea). */
  allowNewlines?: boolean;
  /** Remove HTML/script markup from pasted or typed content. */
  stripHtml?: boolean;
  maxLength?: number;
};

/** Unicode bidi overrides and zero-width chars used in spoofing / hidden payloads. */
const INVISIBLE_UNICODE_PATTERN =
  /[\u200B-\u200D\uFEFF\u202A-\u202E\u2066-\u2069]/g;

const SCRIPT_TAG_PATTERN =
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
const STYLE_TAG_PATTERN =
  /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi;
const HTML_TAG_PATTERN = /<\/?[^>]+(>|$)/g;

const INPUT_TYPES_WITHOUT_SANITIZATION = new Set([
  "checkbox",
  "color",
  "date",
  "datetime-local",
  "file",
  "hidden",
  "month",
  "number",
  "radio",
  "range",
  "time",
  "week",
]);

const INPUT_TYPES_CONTROL_CHARS_ONLY = new Set([
  "email",
  "password",
  "tel",
  "url",
]);

function getControlCharacterPattern(allowNewlines: boolean): RegExp {
  if (allowNewlines) {
    // Keep tab, LF, CR; strip other C0 controls and DEL.
    return /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
  }

  return /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u000A\u000D]/g;
}

export function stripHtmlFromPlainText(value: string): string {
  return value
    .replace(SCRIPT_TAG_PATTERN, "")
    .replace(STYLE_TAG_PATTERN, "")
    .replace(HTML_TAG_PATTERN, "");
}

export function sanitizePlainTextInput(
  value: string,
  options: SanitizeInputOptions = {},
): string {
  const { allowNewlines = false, stripHtml = true, maxLength } = options;

  let result = value
    .replace(INVISIBLE_UNICODE_PATTERN, "")
    .replace(getControlCharacterPattern(allowNewlines), "");

  if (stripHtml) {
    result = stripHtmlFromPlainText(result);
  }

  if (maxLength != null && maxLength >= 0) {
    result = result.slice(0, maxLength);
  }

  return result;
}

export function shouldSanitizeInputType(type?: string): "none" | "control-only" | "full" {
  const normalizedType = (type ?? "text").toLowerCase();

  if (INPUT_TYPES_WITHOUT_SANITIZATION.has(normalizedType)) {
    return "none";
  }

  if (INPUT_TYPES_CONTROL_CHARS_ONLY.has(normalizedType)) {
    return "control-only";
  }

  return "full";
}

export function sanitizeInputValue(
  value: string,
  type?: string,
  options: Omit<SanitizeInputOptions, "stripHtml"> & { stripHtml?: boolean } = {},
): string {
  const mode = shouldSanitizeInputType(type);

  if (mode === "none") {
    return value;
  }

  return sanitizePlainTextInput(value, {
    ...options,
    stripHtml: mode === "full" && options.stripHtml !== false,
  });
}

export function applySanitizedValueToChangeEvent<
  T extends HTMLInputElement | HTMLTextAreaElement,
>(event: ChangeEvent<T>, sanitizedValue: string): ChangeEvent<T> {
  if (sanitizedValue !== event.target.value) {
    event.target.value = sanitizedValue;
  }

  return event;
}
